import { debounce } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'

type MainTab = {
  key: string
  label: string
  renderElement?: JSX.Element
  isError?: boolean
}

export const scrollspyTabs = ({
  tabs,
  parentRef,
  offset,
}: {
  tabs: MainTab[]
  parentRef?: React.RefObject<HTMLDivElement>
  offset?: number
}) => {
  const [activeLink, setActiveLink] = useState(tabs[0].key)
  const [isScrolling, setIsScrolling] = useState(false)
  const [defaultOffset, setDefaultOffset] = useState(offset || 0)
  const parentRefValue = parentRef?.current || window

  // How far down the viewport a section top may sit and still count as active.
  // ~80px was too tight: the prior section stayed selected until only a sliver
  // remained at the top. ~35% switches earlier so the tab matches what's on screen.
  const ACTIVATION_BAND_RATIO = 0.35
  const MIN_ACTIVATION_BAND_PX = 160
  // Ignore tiny remnants of the section above (e.g. 5% still peeking under sticky tabs).
  const MIN_VISIBLE_BOTTOM_RATIO = 0.15

  const handleResize = useCallback(
    debounce(() => {
      if (typeof window !== 'undefined' && window.innerHeight) {
        const activationBand = Math.max(
          MIN_ACTIVATION_BAND_PX,
          Math.round(window.innerHeight * ACTIVATION_BAND_RATIO)
        )
        setDefaultOffset(window.innerHeight - activationBand)
      }
    }, 80),
    []
  )

  useEffect(() => {
    if (offset) {
      return
    }
    handleResize()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [parentRef, tabs, handleResize, offset])

  const scrollToSection = useCallback(
    (index: number) => {
      setIsScrolling(true)
      const sectionEle = tabs[index]
      if (sectionEle) {
        const element = document.getElementById(sectionEle.key)
        if (element) {
          setTimeout(
            () =>
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              }),
            10
          )
          setActiveLink(() => sectionEle.key)

          setTimeout(() => {
            setIsScrolling(false)
          }, 50)
        }
      }
    },
    [tabs]
  )

  const isElementInViewport = (el: HTMLElement, offsetValue: number) => {
    const { top, bottom } = el.getBoundingClientRect()
    const vHeight = parentRef?.current
      ? parentRef?.current?.clientHeight
      : window.innerHeight || document.documentElement.clientHeight
    const minVisibleBottom = offset
      ? 0
      : Math.round(vHeight * MIN_VISIBLE_BOTTOM_RATIO)
    return bottom >= minVisibleBottom && top + offsetValue <= vHeight
  }

  const determineActiveSection = useCallback(() => {
    if (!isScrolling) {
      for (let i = tabs.length - 1; i >= 0; i--) {
        const section = document.getElementById(tabs[i].key)
        if (section) {
          if (isElementInViewport(section, defaultOffset)) {
            setActiveLink(tabs[i].key)
            break
          }
        }
      }
    }
  }, [tabs, isScrolling, defaultOffset])

  useEffect(() => {
    const _determineActiveSection = debounce(determineActiveSection, 250)
    if (typeof window !== 'undefined') {
      parentRefValue?.removeEventListener(
        'scroll',
        _determineActiveSection,
        true
      )
      parentRefValue?.addEventListener('scroll', _determineActiveSection, true)
    }

    return () => {
      if (typeof window !== 'undefined') {
        parentRefValue?.removeEventListener(
          'scroll',
          _determineActiveSection,
          true
        )
      }
    }
  }, [parentRefValue, determineActiveSection])

  const activeTabInex = tabs.findIndex(tabItem => tabItem.key === activeLink)
  return {
    scrollToSection,
    activeLink,
    activeTabInex,
  }
}
